"use client";

import {
  EmptyState,
  Icon,
  PageHeader,
  PageLayout,
  SectionCard,
  TrustpilotFeedbackModal,
  capitalize,
  useAppContext,
  useFeedbackTrigger,
} from "@repo/dashboard-core";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";
import { useCallback, useEffect, useState } from "react";

import { RecentEsims } from "@/components/esim/RecentEsims";
import { TopUpCard } from "@/components/topup/TopUpCard";
import { TopUpCardSkeleton } from "@/components/topup/TopUpCardSkeleton";
import { TrustpilotReviewCard } from "@/components/topup/TrustpilotReviewCard";
import {
  getTrustpilotInvitationLink,
  submitFeedback,
} from "@repo/dashboard-core/actions";
import { useBrand } from "@/lib/context/BrandContext";
import { usePurchases } from "@/lib/context/PurchasesContext";
import { useTrustpilotSent } from "@/lib/context/ServiceReviewContext";
import { reviewableTopUp } from "@/lib/reviewCollector";
import { awaitsDelivery, isOngoing } from "@/lib/types/topup";

const DELIVERY_LOOK_MS = 5_000;
const DELIVERY_LOOKS = 24;

export default function MyTopUpsPage() {
  const t = useTranslations("dashboard");
  const { userInfo, subscriptionsInfo } = useAppContext();
  const locale = useLocale();
  const brand = useBrand();

  // GD-135 — what the customer bought decides which home this is (see
  // `isEsimHome`); each home lists its own product first and the other one
  // after it, when there is any.
  const { topUps, esims, esimsFailed, loading, esimHome, reload } =
    usePurchases();

  // The shell loaded the lists already on the first visit (this joins that
  // load); coming back home picks up top-ups delivered in the meantime.
  useEffect(() => {
    reload();
  }, [reload]);

  // GD-125 — a member sent here straight after paying can land while the
  // top-up is still Ongoing: look again every 5 s, for up to 2 minutes, so the
  // review card and the PIN show up without a reload. Deliveries take seconds;
  // one look 30 s later left members who reloaded first believing the card
  // only came with a reload.
  const waitingForDelivery = awaitsDelivery(topUps);
  useEffect(() => {
    if (!waitingForDelivery) return;
    let looks = 0;
    const timer = setInterval(() => {
      reload();
      looks += 1;
      if (looks === DELIVERY_LOOKS) clearInterval(timer);
    }, DELIVERY_LOOK_MS);
    return () => clearInterval(timer);
  }, [waitingForDelivery, reload]);

  // A top-up card opens to show what was bought and asks for the rating 3 s
  // later; an eSIM row has nothing to open, so it asks at once — on either home.
  const magicLinkId = useSearchParams().get("id");
  const ratesAnEsim =
    !!magicLinkId && esims.some((esim) => esim.applicationId === magicLinkId);

  const { applicationId, feedbackOpened, handleTrigger, handleClose } =
    useFeedbackTrigger({ delay: ratesAnEsim ? 0 : 3000 });

  // GD-120 — once the member has been sent to Trustpilot, from a service or
  // from here, on any device, nothing asks again.
  const { trustpilotSent, markSentToTrustpilot } = useTrustpilotSent();
  const askForFeedback = useCallback(
    () => !trustpilotSent() && handleTrigger(),
    [trustpilotSent, handleTrigger],
  );

  // GD-53 — the reassurance FAQ only goes on the latest ongoing top-up, which
  // is the first ongoing card of the list (top-ups are sorted newest first).
  const faqTuId = topUps.find(isOngoing)?.tuId;

  // GD-125 — Trustpilot review card, in the subscription's first 3 days, for
  // the newest delivered top-up.
  const reviewTopUp = reviewableTopUp(
    topUps,
    subscriptionsInfo?.runningSubscriptions,
  );

  // The purchase a star was picked for in the card. GD-144 — only a pick stops
  // the feedback modal: a card that merely shows took it away from every
  // member who had just paid, all of them in their first 3 days.
  const [pickedInCard, setPickedInCard] = useState<string>();

  const formatDate = useCallback(
    (dateStr: string) => {
      if (!dateStr) return "";
      return new Date(dateStr).toLocaleDateString(locale, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    },
    [locale],
  );

  const recentTopUps = (
    <SectionCard title={t("recent-top-ups")}>
      {/* The columns each TopUpCard lines up on: name, amounts, status and
          date, "View details". */}
      <div className="space-y-1 sm:grid sm:grid-cols-[minmax(0,1fr)_auto_auto_auto] sm:gap-x-4 sm:gap-y-1 sm:space-y-0">
        {topUps.map((topUp) => (
          <TopUpCard
            key={topUp.tuId}
            title={topUp.name}
            image={topUp.logoUrl}
            date={formatDate(topUp.createdAt)}
            pinDetails={topUp.pinDetails}
            amount={
              topUp.amount ? `${topUp.currency} ${topUp.amount}` : undefined
            }
            deliveryAmount={
              topUp.deliveredAmount
                ? `${topUp.deliveredAmountCurrency} ${topUp.deliveredAmount}`
                : undefined
            }
            isOngoing={isOngoing(topUp)}
            showFaq={topUp.tuId === faqTuId}
            // A star picked in the review card answered for this purchase, so
            // the feedback modal doesn't ask again.
            onExpand={
              topUp.applicationId === applicationId &&
              topUp.applicationId !== pickedInCard
                ? askForFeedback
                : undefined
            }
          />
        ))}
      </div>
    </SectionCard>
  );

  // Holds its own empty and error states, which only the eSIM home shows: the
  // top-up home places it only when there are eSIMs.
  const recentEsims = (
    <RecentEsims
      esims={esims}
      failed={esimsFailed}
      feedbackApplicationId={applicationId}
      onFeedback={askForFeedback}
    />
  );

  return (
    <PageLayout>
      <div className="space-y-8">
        <PageHeader
          title={
            userInfo.firstName
              ? `${t("welcome")}, ${capitalize(userInfo.firstName)}!`
              : `${t("welcome")}!`
          }
          description={esimHome ? t("esim.tagline") : t("tagline")}
        />

        {loading ? (
          <SectionCard>
            <TopUpCardSkeleton />
          </SectionCard>
        ) : esimHome ? (
          <>
            {recentEsims}
            {topUps.length > 0 && recentTopUps}
          </>
        ) : (
          <>
            {/* CTA Banner */}
            <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-primary to-primary/80 p-6 text-white shadow-shell-lg md:p-8">
              <div className="relative z-10 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h3 className="text-lg font-bold">{t("cta-title")}</h3>
                  <p className="mt-1 text-sm text-white/80">
                    {t("cta-description")}
                  </p>
                </div>
                <Link
                  href={`/${locale}/library`}
                  className="inline-flex shrink-0 items-center justify-center rounded-lg bg-white px-6 py-2.5 text-sm font-semibold text-primary transition-colors hover:bg-white/90"
                >
                  {t("cta-button")}
                </Link>
              </div>
              {/* Decorative circles */}
              <div className="absolute -right-6 -top-6 h-32 w-32 rounded-full bg-white/10" />
              <div className="absolute -bottom-4 -right-4 h-20 w-20 rounded-full bg-white/5" />
            </div>

            {/* Keyed by purchase: Trustpilot's widget takes its customer only
                once, so a top-up delivered while the card is up needs a new
                widget, not the old one's customer. */}
            {reviewTopUp && (
              <TrustpilotReviewCard
                key={reviewTopUp.applicationId}
                applicationId={reviewTopUp.applicationId}
                onPicked={setPickedInCard}
              />
            )}

            {topUps.length === 0 ? (
              <SectionCard>
                <EmptyState
                  icon={<Icon name="send" size={28} />}
                  title={t("no-top-ups")}
                  description={t("no-top-ups-description")}
                />
              </SectionCard>
            ) : (
              recentTopUps
            )}
            {esims.length > 0 && recentEsims}
          </>
        )}
      </div>

      {applicationId && (
        <TrustpilotFeedbackModal
          opened={feedbackOpened}
          onClose={handleClose}
          applicationId={applicationId}
          trustpilotWebsiteId={brand.trustpilotWebsiteId}
          domain={brand.domain}
          submitFeedback={submitFeedback}
          getTrustpilotInvitationLink={getTrustpilotInvitationLink}
          redirectFrom={4}
          onRedirected={markSentToTrustpilot}
        />
      )}
    </PageLayout>
  );
}
